import { storeToRefs } from "pinia";
import { Notify } from "quasar";
import { computed, inject, nextTick, onBeforeUnmount, ref, toRefs } from "vue";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import useMention from "shared/composables/useMention";
import { POPOUT_PLAYER_TARGET } from "shared/constants";
import { duration, toSeconds } from "shared/helpers/date";
import { IsPublicInsightsSymbol } from "shared/modules/Charts/types/symbols";
import { PodcastEpisode } from "shared/resources";
import { useUniversalPlayerStore } from "shared/stores/universalPlayer";
import { isKinetiqMention } from "shared/types/mentions/guards";

export default function usePodcastPlayer(props, context, popoutName) {
  const isDesktop = inject("isDesktop");
  const isPublicInsights = inject(IsPublicInsightsSymbol, false);
  const { mention = ref({}) } = toRefs(props);

  const universalPlayerStore = useUniversalPlayerStore();

  const { playerInitialClip, playerOpen, playerPopoutRef } =
    storeToRefs(universalPlayerStore);

  const {
    playerInsertClip,
    playerShow,
    setPlayerDismissed,
    setPlayerPopoutTarget,
  } = universalPlayerStore;

  const { playsOnTveyes } = useMention(props, context);

  const playsOnKinetiq = computed(() => isKinetiqMention(mention.value));

  const podcastEpisodeFragments = ref([]);
  const loadingFragments = ref(false);

  const clip = ref({
    ...mention.value,
    media_url: "",
    signed_url: "",
    start_time: mention.value.timestamp,
  });

  const podcastStartTimestamp = computed(() => mention.value.timestamp);

  const podcastEndTimestamp = computed(() => {
    const lastFragment = podcastEpisodeFragments.value.slice(-1).pop();

    return (
      podcastStartTimestamp.value + toSeconds(lastFragment?.endTimeMs || 1000)
    );
  });

  const podcastDuration = computed(
    () => podcastEndTimestamp.value - podcastStartTimestamp.value
  );

  const hasDuration = computed(() => podcastDuration.value > 1);

  const formattedPodcastDuration = computed(() =>
    duration(podcastEndTimestamp.value, podcastStartTimestamp.value)
  );

  async function loadFragments() {
    try {
      loadingFragments.value = true;

      const episodeDetails = (
        await PodcastEpisode.includes(["podcastEpisodeFragments"]).find(
          mention.value.id
        )
      ).data;

      podcastEpisodeFragments.value = episodeDetails.podcastEpisodeFragments;
    } catch {
      Notify.create({
        message: getLocaleText("podcast_player.load_podcast_fragments_error"),
        type: "negative",
      });
    } finally {
      loadingFragments.value = false;
    }
  }

  async function loadClipInformation() {
    if (playsOnKinetiq.value || playsOnTveyes.value || isPublicInsights) return;
    if (podcastEpisodeFragments.value.length) return;

    try {
      await loadFragments();

      clip.value = {
        ...mention.value,
        ...clip.value,
        end_time: podcastEndTimestamp.value,
        start_time: podcastStartTimestamp.value,
      };
    } catch {
      Notify.create({
        message: getLocaleText("podcast_player.load_podcast_information_error"),
        type: "negative",
      });
    }
  }

  function setPlayerTarget(playInComponent) {
    if (playInComponent) {
      setPlayerPopoutTarget(popoutName);

      track("Playing the episode fragments within the component", {
        type: "podcast",
      });
    } else {
      setPlayerPopoutTarget(POPOUT_PLAYER_TARGET);

      track("Playing the episode fragments in the popout player", {
        type: "podcast",
      });
    }
  }

  function launchPlayer(playInComponent = true) {
    if (playsOnKinetiq.value) return;

    playerInsertClip(clip.value);
    if (!playerOpen.value) playerShow();

    nextTick(() => {
      playerPopoutRef.value?.playNow(clip.value);
    });

    setPlayerTarget(playInComponent);
    setPlayerDismissed(false);
  }

  function isPopoutPlayerPlayingThisClip() {
    return (
      playerInitialClip.value &&
      clip.value &&
      playerInitialClip.value.id === clip.value.id
    );
  }

  onBeforeUnmount(() => {
    if (isPopoutPlayerPlayingThisClip() && playerOpen.value && isDesktop) {
      setPlayerPopoutTarget(POPOUT_PLAYER_TARGET);

      track("Playing the clip in the popout player", {
        type: "podcast",
      });
    }
  });

  return {
    clip,
    formattedPodcastDuration,
    hasDuration,
    isPopoutPlayerPlayingThisClip,
    launchPlayer,
    loadClipInformation,
    loadingFragments,
    playsOnKinetiq,
    podcastDuration,
    podcastEndTimestamp,
    podcastEpisodeFragments,
    podcastStartTimestamp,
    setPlayerTarget,
  };
}
